import { getColor } from '@assets/theme';
import useLayoutContext from '@hooks/useLayoutContext';
import { Close, KeyboardOutlined } from '@mui/icons-material';
import { Box, debounce, Fab, IconButton, Stack } from '@mui/material';
import { useAppSelector } from '@store/index';
import { getKeyboardHandler } from '@utils/keyboard-registry';
import { usePOSScreen, useValidateDesktopScreen } from '@utils/screen-size';
import { motion } from 'framer-motion';
import {
  FC,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';

interface IVirtualKeyboard {
  constraintsRef: React.MutableRefObject<null>;
  isComponent?: boolean;
}

interface IOSKeyboard {
  keyboardRef: MutableRefObject<any>;
  type?: 'numericKeyboard' | 'default';
  setSecCode?: React.Dispatch<React.SetStateAction<string>>;
  otpInputRef?: React.MutableRefObject<HTMLDivElement | null>;
}
/**
 * Computes the next value based on key press and selection.
 */
const computeNextValue = (
  current: string,
  button: string,
  start: number,
  end: number,
): string => {
  if (button === '{bksp}') {
    if (start === end && start > 0) {
      return current.slice(0, start - 1) + current.slice(end);
    }
    return current.slice(0, start) + current.slice(end);
  }

  return current.slice(0, start) + button + current.slice(end);
};

/**
 * Determines whether a key is a printable character.
 */
const isPrintableKey = (button: string): boolean => {
  return !button.startsWith('{');
};

/**
 * Commits a value to an input exactly like native keyboard typing.
 * This guarantees compatibility with React, RHF, and controlled inputs.
 */
const commitValue = (
  el: HTMLInputElement | HTMLTextAreaElement,
  value: string,
) => {
  const prototype = Object.getPrototypeOf(el);
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;

  setter?.call(el, value);

  el.dispatchEvent(
    new InputEvent('input', {
      bubbles: true,
      inputType: 'insertText',
      data: value,
    }),
  );

  el.dispatchEvent(new Event('change', { bubbles: true }));
};

/**
 * Virtual on-screen keyboard component.
 * Simulates native keyboard behavior instead of mutating DOM directly.
 */
export const OnScreenKeyboard: FC<IOSKeyboard> = ({
  keyboardRef,
  type,
  setSecCode,
  otpInputRef,
}) => {
  const { activeTargetId } = useAppSelector((state) => state.keyboard);
  const handler = getKeyboardHandler(activeTargetId ?? '');

  const [layoutName, setLayoutName] = useState('numericKeyboard');

  useEffect(() => {
    if (type) setLayoutName(type);
  }, [type]);

  /**
   * Handles a single virtual key press.
   */
  const handleKeyPress = (button: string) => {
    /* ---------------- Layout toggles ---------------- */
    if (['{shift}', '{default}', '{numericKeyboard}'].includes(button)) {
      setLayoutName((prev) =>
        button === '{shift}'
          ? prev === 'default'
            ? 'shift'
            : 'default'
          : button.replace(/[{}]/g, ''),
      );
      return;
    }

    const activeElement = document.activeElement as
      | HTMLInputElement
      | HTMLTextAreaElement
      | null;

    if (!activeElement) return;

    /* ---------------- OTP handling ---------------- */
    if (otpInputRef?.current?.contains(activeElement)) {
      const inputs = otpInputRef.current.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement
      >('input');
      const currentIndex = Array.from(inputs).indexOf(activeElement);

      if (currentIndex === -1) return;

      if (button === '{arrowleft}' && currentIndex > 0) {
        inputs[currentIndex - 1].focus();
        return;
      }

      if (button === '{arrowright}' && currentIndex < inputs.length - 1) {
        inputs[currentIndex + 1].focus();
        return;
      }

      if (button === '{bksp}') {
        setSecCode?.((prev) => {
          const arr = prev.split('');
          arr[currentIndex] = '';
          if (currentIndex > 0) inputs[currentIndex - 1].focus();
          return arr.join('');
        });
        return;
      }

      if (!button.startsWith('{')) {
        setSecCode?.((prev) => {
          const arr = prev.split('');
          arr[currentIndex] = button;
          if (currentIndex < inputs.length - 1) {
            inputs[currentIndex + 1].focus();
          }
          return arr.join('');
        });
      }

      return;
    }

    /* ---------------- Normal input handling ---------------- */
    const isNumberFormat = activeElement.dataset.type === 'number-format';

    const current = handler?.getValue?.() ?? activeElement.value ?? '';

    const start = activeElement.selectionStart ?? current.length;
    const end = activeElement.selectionEnd ?? current.length;

    /* Arrow navigation */
    if (button === '{arrowleft}') {
      activeElement.setSelectionRange(
        Math.max(0, start - 1),
        Math.max(0, start - 1),
      );
      return;
    }

    if (button === '{arrowright}') {
      activeElement.setSelectionRange(
        Math.min(current.length, start + 1),
        Math.min(current.length, start + 1),
      );
      return;
    }

    if (button === '{arrowup}' || button === '{arrowdown}') return;

    /* Ignore non-printable keys */
    if (!isPrintableKey(button) && button !== '{bksp}') return;

    const nextValue = computeNextValue(current, button, start, end);

    /* NumberFormat / RHF-controlled input */
    if (isNumberFormat && handler?.inputRef) {
      commitValue(handler.inputRef, nextValue);
      return;
    }

    /* Plain input fallback */
    commitValue(activeElement, nextValue);

    /* Restore caret */
    setTimeout(() => {
      const caret = start + (button === '{bksp}' ? -1 : button.length);
      activeElement.setSelectionRange(Math.max(0, caret), Math.max(0, caret));
    }, 0);
  };

  const onKeyPress = useMemo(() => debounce(handleKeyPress, 60), [layoutName]);

  return (
    <Keyboard
      keyboardRef={(r) => (keyboardRef.current = r)}
      layoutName={layoutName}
      onKeyPress={onKeyPress}
      preventMouseDownDefault
      layout={{
        default: [
          'q w e r t y u i o p',
          'a s d f g h j k l',
          '{shift} z x c v b n m . {arrowup} {bksp}',
          '{numericKeyboard} {space} {arrowleft} {arrowdown} {arrowright}',
        ],
        shift: [
          'Q W E R T Y U I O P',
          'A S D F G H J K L',
          '{shift} Z X C V B N M . {arrowup} {bksp}',
          '{numericKeyboard} {space} {arrowleft} {arrowdown} {arrowright}',
        ],
        numericKeyboard: [
          '1 2 3',
          '4 5 6',
          '7 8 9',
          '{default} 0 .',
          '{arrowleft} {arrowright} {bksp}',
        ],
      }}
      display={{
        '{numericKeyboard}': '123',
        '{default}': 'A',
        '{bksp}': `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                    <path d="M22.6665 3H7.6665C6.9765 3 6.4365 3.35 6.0765 3.88L0.666504 12L6.0765 20.11C6.4365 20.64 6.9765 21 7.6665 21H22.6665C23.7665 21 24.6665 20.1 24.6665 19V5C24.6665 3.9 23.7665 3 22.6665 3ZM19.6665 15.59L18.2565 17L14.6665 13.41L11.0765 17L9.6665 15.59L13.2565 12L9.6665 8.41L11.0765 7L14.6665 10.59L18.2565 7L19.6665 8.41L16.0765 12L19.6665 15.59Z" fill="#212B36"/>
                  </svg>`,
        '{shift}': `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                      <path d="M4.33325 12L5.74325 13.41L11.3333 7.83V20H13.3333V7.83L18.9133 13.42L20.3333 12L12.3333 4L4.33325 12Z" fill="white"/>
                    </svg>`,
        '{space}': 'SPACE',
        '{arrowup}': `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 4l-8 8h6v8h4v-8h6z"/>
      </svg>
    `,
        '{arrowdown}': `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 20l8-8h-6v-8h-4v8h-6z"/>
      </svg>
    `,
        '{arrowleft}': `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
        <path d="M4 12l8 8v-6h8v-4h-8v-6z"/>
      </svg>
    `,
        '{arrowright}': `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20 12l-8-8v6h-8v4h8v6z"/>
      </svg>
    `,
      }}
    />
  );
};

const VirtualKeyboard = ({
  constraintsRef,
  isComponent = false,
}: IVirtualKeyboard) => {
  const { virtualKeyboard } = useLayoutContext();

  if (!virtualKeyboard && !isComponent) return null;

  const { white, primary, badge } = getColor();
  const [isOpen, setIsOpen] = useState(false);
  const keyboard = useRef(null);

  const isPOS1024p = usePOSScreen();
  const isDesktopScreen = useValidateDesktopScreen();

  return (
    <>
      <Stack
        direction='row'
        gap='10px'
        alignItems='center'
        sx={{
          position: 'absolute',
          bottom: '20px',
          left: '24px',
        }}
      >
        {/* VIRTUAL KEYBOARD BUTTON */}
        {isDesktopScreen && (
          <Fab
            variant='extended'
            size={isPOS1024p ? 'small' : 'medium'}
            color='primary'
            onClick={() => setIsOpen(!isOpen)}
            sx={{
              borderRadius: '100px',
              zIndex: 9999,
              height: isPOS1024p ? '46px' : '56px',
              width: isPOS1024p ? '46px' : '56px',
              fontSize: '24px',
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            <KeyboardOutlined fontSize='inherit' />
          </Fab>
        )}
      </Stack>

      {/* VIRTUAL KEYBOARD */}
      {isOpen && (
        <Box
          component={motion.div}
          drag
          dragConstraints={constraintsRef}
          dragMomentum={false}
          initial={{ left: '24px', bottom: '88px' }}
          sx={{
            zIndex: 9999,
            position: 'absolute',
            cursor: 'move',
            boxShadow:
              ' 0px 5px 26px 4px rgba(0, 0, 0, 0.12), 0px 14px 21px 2px rgba(0, 0, 0, 0.14), 0px 7px 9px -4px rgba(0, 0, 0, 0.20)',
            borderRadius: '12px',

            // LAYOUTS
            '.hg-theme-default': {
              fontFamily: 'inherit!important',
              fontWeight: 500,
              bgcolor: `${white}!important`,
              padding: 2,
            },
            '.hg-layout-default , .hg-layout-shift': {
              width: '900px',
            },
            '.hg-layout-numericKeyboard': {
              width: '300px',
            },
            '.hg-row': {
              columnGap: '7px',
            },
            '.hg-rows': {
              display: 'flex',
              flexDirection: 'column',
              rowGap: '8px',
            },

            // ALL BUTTONS
            '.hg-functionBtn, .hg-standardBtn, .hg-button-bksp, .hg-button-numericKeyboard, .hg-button-space, .hg-button-default, .hg-button-shift':
              {
                bgcolor: `${badge}!important`,
              },
            '.hg-activeButton': {
              bgcolor: `#EFEFEF!important`,
            },
            '.hg-button-default': {
              maxWidth: '81px',
            },
            '.hg-button': { px: '14px!important' },
            '.hg-button-space': { width: '485px' },
            '.hg-button-bksp': { width: '47px' },

            // NUMERIC BUTTON
            '.hg-button-numericKeyboard': {
              maxWidth: '52px',
            },

            // SHIFT BUTTON
            '.hg-button-bksp span, .hg-button-shift span,': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            '.hg-button-shift': {
              maxWidth: '72px',
            },
            '.hg-button-shift svg path': {
              fill: '#000000de!important',
            },
            '.hg-layout-shift .hg-button-shift': {
              bgcolor: `${primary}!important`,
            },
            '.hg-layout-shift .hg-button-shift  svg path': {
              bgcolor: `${primary}!important`,
              fill: '#FFF!important',
            },
          }}
        >
          <IconButton
            size='small'
            onClick={() => setIsOpen(false)}
            sx={{
              position: 'absolute',
              top: -32,
              right: -32,
              bgcolor: primary,
              color: '#FFF',
              ':hover': {
                bgcolor: primary,
              },
            }}
          >
            <Close />
          </IconButton>
          <OnScreenKeyboard keyboardRef={keyboard} />
        </Box>
      )}
    </>
  );
};

export default VirtualKeyboard;
